import React, { useState } from 'react';
import { PRE_SPARRING_QUESTIONS, DOMAIN_LABELS } from './preSparringQuestionsData';
import { LanguageVariantSelector, useLanguageVariant, QuestionVariants } from '../shared';

interface PreSparringQuestionsProps {
  onComplete: (answers: Record<string, string>) => void;
  onSkip: () => void;
}

/**
 * PreSparringQuestions
 * 
 * Opt-in vragenlijst vóór sparring start (context gathering).
 * Antwoorden zijn optioneel, geen validatie, geen opslag.
 */
const PreSparringQuestions: React.FC<PreSparringQuestionsProps> = ({
  onComplete,
  onSkip,
}) => {
  const [variant, setVariant] = useLanguageVariant();
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleComplete = () => {
    // Filter out empty answers
    const filteredAnswers = Object.entries(answers)
      .filter(([_, value]) => value.trim() !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    
    onComplete(filteredAnswers);
  };

  const getQuestionText = (variants: QuestionVariants): string => {
    return variants[variant];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90dvh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Context verzamelen
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Deze vragen helpen de sparring gerichter te maken. Alles is optioneel.
          </p>
        </div>

        {/* Language variant selector */}
        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50">
          <LanguageVariantSelector
            currentVariant={variant}
            onVariantChange={setVariant}
          />
        </div>

        {/* Content - Always show scrollbar */}
        <div className="flex-1 overflow-y-scroll px-6 py-6 space-y-6 scrollbar-always">
          {PRE_SPARRING_QUESTIONS.map((q) => (
            <div key={q.id} className="space-y-2">
              <label className="block">
                <span className="text-sm font-medium text-gray-700">
                  {DOMAIN_LABELS[q.id]}
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  {getQuestionText(q.question)}
                </p>
                <textarea
                  value={answers[q.id] || ''}
                  onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                  placeholder="Typ hier je antwoord (optioneel)..."
                  rows={3}
                  className="
                    mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg
                    text-sm text-gray-900 placeholder-gray-400
                    focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent
                    resize-y overflow-y-auto scrollbar-always min-h-[80px]
                  "
                />
              </label>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={onSkip}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Overslaan
          </button>
          <button
            onClick={handleComplete}
            className="
              px-6 py-2 bg-gray-900 text-white rounded-lg
              hover:bg-gray-800 transition-colors
            "
          >
            Verder naar sparring
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreSparringQuestions;
